import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Prompt } from 'react-router';
import { fetchCategoryConfig, saveCategoryConfig } from '../../../actions/admin/models/event.clearance';
import { formDataSelector } from '../../../selectors/admin/forms';
import { MODELS } from '../../../constants/admin/settings';
import {
    sortSelector,
    sortDirectionSelector,
    searchQuerySelector,
    selectedSelector
} from '../../../selectors/admin/tables';
import { select, resetSelection } from '../../../actions/admin/tables';
import { blockTabSwitch, enableTabSwitch } from '../../../actions/admin/tabs';
import {
    filterCategories,
    sortCategories,
    filterLabels,
    sortLabels,
    createNewCategory,
    createNewLabel
} from '../../../lib/clearance.utils';
import { ARE_YOU_SURE_TEXT } from '../../../constants/texts/errors';
import CategoriesOverview from './CategoriesOverview';
import CategoryDetails from './CategoryDetails';
import { getText } from '../../../constants/admin/texts';
import ConfirmationModal from '../../../components/admin/forms/ConfirmationModal';

class CategoryConfig extends Component {
    state = {
        selectedCategory: null,
        originalCategory: null,
        showDeleteConfirm: false
    };

    componentDidMount() {
        if (this.props.eventId) {
            this.props.fetchCategoryConfig(this.props.eventId);
        }
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        window.addEventListener('popstate', this.handlePopState);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.eventId && this.props.eventId !== prevProps.eventId) {
            this.props.fetchCategoryConfig(this.props.eventId);
        }

        // Update tab switch blocking based on unsaved changes
        const hadUnsavedChanges = this.checkUnsavedChanges(prevState);
        const hasUnsavedChanges = this.hasUnsavedChanges();
        if (hasUnsavedChanges && !hadUnsavedChanges) {
            this.props.blockTabSwitch();
        } else if (!hasUnsavedChanges && hadUnsavedChanges) {
            this.props.enableTabSwitch();
        }
    }

    checkUnsavedChanges = state => {
        const { selectedCategory, originalCategory } = state;
        if (!selectedCategory || !originalCategory) return false;
        return JSON.stringify(selectedCategory) !== JSON.stringify(originalCategory);
    };

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        window.removeEventListener('popstate', this.handlePopState);

        // Re-enable tab switching when component unmounts
        this.props.enableTabSwitch();
    }

    handleBeforeUnload = e => {
        if (this.hasUnsavedChanges()) {
            e.preventDefault();
            e.returnValue = '';
        }
    };

    hasUnsavedChanges = () => {
        const { selectedCategory, originalCategory } = this.state;
        if (!selectedCategory || !originalCategory) return false;
        return JSON.stringify(selectedCategory) !== JSON.stringify(originalCategory);
    };

    handleOpenCategory = item => {
        const categoryClone = JSON.parse(JSON.stringify(item));

        const newUrl = `${window.location.pathname}?category=${item.id}${window.location.hash}`;
        window.history.pushState(null, '', newUrl);

        this.setState({
            selectedCategory: item,
            originalCategory: categoryClone
        });
    };

    handleCloseCategory = () => {
        const newUrl = `${window.location.pathname}${window.location.hash}`;
        window.history.pushState(null, '', newUrl);
        this.setState({ selectedCategory: null, originalCategory: null });
    };

    isCategoryFormValid = () => {
        const { selectedCategory } = this.state;
        if (!selectedCategory) return false;
        const hasCategoryLabel = !!selectedCategory.label?.trim();
        const allLabelsHaveLabel = (selectedCategory.labels || []).every(l => !!(l.label || '').trim());
        return hasCategoryLabel && allLabelsHaveLabel;
    };

    handleSaveCategory = () => {
        const { selectedCategory } = this.state;
        const { categoryConfig, eventId } = this.props;

        if (!this.isCategoryFormValid()) return;

        const existingCategories = categoryConfig?.categories || [];
        const isNewCategory = selectedCategory.id.startsWith('new-');

        const cleanedLabels = selectedCategory.labels.map(label => ({
            ...label,
            id: label.id.startsWith('new-label-') ? undefined : label.id
        }));

        let updatedCategories;
        if (isNewCategory) {
            const newCategory = {
                ...selectedCategory,
                id: undefined,
                labels: cleanedLabels
            };
            updatedCategories = [...existingCategories, newCategory];
        } else {
            updatedCategories = existingCategories.map(cat =>
                cat.id === selectedCategory.id
                    ? {
                          ...selectedCategory,
                          labels: cleanedLabels
                      }
                    : cat
            );
        }

        const updatedConfig = {
            ...categoryConfig,
            eventId,
            categories: updatedCategories
        };

        this.props.saveCategoryConfig(eventId, updatedConfig);

        const newUrl = `${window.location.pathname}${window.location.hash}`;
        window.history.pushState(null, '', newUrl);

        this.setState({ selectedCategory: null, originalCategory: null });
    };

    handleDeleteCategory = () => {
        this.setState({ showDeleteConfirm: true });
    };

    handleConfirmDeleteCategory = () => {
        const { selectedCategory } = this.state;
        const { categoryConfig, eventId } = this.props;

        const existingCategories = categoryConfig?.categories || [];
        const updatedCategories = existingCategories.filter(cat => cat.id !== selectedCategory.id);

        const updatedConfig = {
            ...categoryConfig,
            eventId,
            categories: updatedCategories
        };

        this.props.saveCategoryConfig(eventId, updatedConfig);

        const newUrl = `${window.location.pathname}${window.location.hash}`;
        window.history.pushState(null, '', newUrl);

        this.setState({ selectedCategory: null, originalCategory: null, showDeleteConfirm: false });
    };

    handleCancelDeleteCategory = () => {
        this.setState({ showDeleteConfirm: false });
    };

    handleAddCategory = () => {
        const newCategory = createNewCategory();
        const categoryClone = JSON.parse(JSON.stringify(newCategory));
        const newUrl = `${window.location.pathname}?category=${newCategory.id}${window.location.hash}`;
        window.history.pushState(null, '', newUrl);
        this.setState({
            selectedCategory: newCategory,
            originalCategory: categoryClone
        });
    };

    handleCategoryFieldChange = (field, value) => {
        this.setState({
            selectedCategory: {
                ...this.state.selectedCategory,
                [field]: value
            }
        });
    };

    handleVisibilityChange = (viewType, checked) => {
        const { selectedCategory } = this.state;
        let visibleInViews = [...(selectedCategory.visibleInViews || [])];

        if (checked && !visibleInViews.includes(viewType)) {
            visibleInViews.push(viewType);
        } else if (!checked && visibleInViews.includes(viewType)) {
            visibleInViews = visibleInViews.filter(v => v !== viewType);
        }

        this.setState({
            selectedCategory: {
                ...selectedCategory,
                visibleInViews
            }
        });
    };

    handleLabelFieldChange = (label, field, newValue) => {
        const { selectedCategory } = this.state;
        const updatedLabels = selectedCategory.labels.map(l => (l.id === label.id ? { ...l, [field]: newValue } : l));
        this.setState({
            selectedCategory: { ...selectedCategory, labels: updatedLabels }
        });
    };

    handleDefaultSelectedChange = label => {
        const { selectedCategory } = this.state;
        const updatedLabels = selectedCategory.labels.map(l => ({
            ...l,
            defaultSelected: l.id === label.id
        }));
        this.setState({
            selectedCategory: { ...selectedCategory, labels: updatedLabels }
        });
    };

    handleAddLabel = () => {
        const { selectedCategory } = this.state;
        const currentLabels = selectedCategory.labels || [];
        const newLabel = createNewLabel();

        this.setState(
            {
                selectedCategory: {
                    ...selectedCategory,
                    labels: [...currentLabels, newLabel]
                }
            },
            () => {
                setTimeout(() => {
                    const newInput = document.querySelector(`.ckp-label-text-input[data-label-id="${newLabel.id}"]`);
                    if (newInput) {
                        newInput.focus();
                    }
                }, 50);
            }
        );
    };

    handleBulkDeleteCategories = () => {
        const { categoryConfig, eventId, categorySelection } = this.props;

        const existingCategories = categoryConfig?.categories || [];
        const updatedCategories = existingCategories.filter(cat => !categorySelection.includes(cat.id));

        const updatedConfig = {
            ...categoryConfig,
            eventId,
            categories: updatedCategories
        };

        this.props.saveCategoryConfig(eventId, updatedConfig);
        this.props.resetSelection(2);
    };

    handleCategoryPositionChange = (category, position) => {
        if (position === '' || position === null || position === undefined || isNaN(position) || position < 0) {
            return;
        }

        const { categoryConfig, eventId } = this.props;
        const existingCategories = categoryConfig?.categories || [];

        const updatedCategories = existingCategories.map(cat => (cat.id === category.id ? { ...cat, position } : cat));

        const updatedConfig = {
            ...categoryConfig,
            eventId,
            categories: updatedCategories
        };

        this.props.saveCategoryConfig(eventId, updatedConfig);
    };

    handleBulkDeleteLabels = () => {
        const { labelSelection } = this.props;
        const { selectedCategory } = this.state;

        if (!labelSelection || labelSelection.length === 0) return;

        let updatedLabels = selectedCategory.labels.filter(label => !labelSelection.includes(label.id));

        // Check if the default label was deleted
        const hasDefaultLabel = updatedLabels.some(l => l.defaultSelected);

        // If default was deleted and there are still labels remaining, set a new default
        if (!hasDefaultLabel && updatedLabels.length > 0) {
            // First, try to find a non-hidden label
            const firstNonHidden = updatedLabels.find(l => l.label.toLowerCase() !== 'hidden');
            const newDefaultLabel = firstNonHidden || updatedLabels[0];
            updatedLabels = updatedLabels.map(l => ({
                ...l,
                defaultSelected: l.id === newDefaultLabel.id
            }));
        }

        this.setState({
            selectedCategory: {
                ...selectedCategory,
                labels: updatedLabels
            }
        });
        this.props.resetSelection(1);
    };

    getLabelTableData = () => {
        const { selectedCategory } = this.state;
        if (!selectedCategory) return [];

        const { labelSearchQuery, labelSort, labelSortDirection } = this.props;
        const labels = selectedCategory.labels || [];
        const filteredLabels = filterLabels(labels, labelSearchQuery);

        const direction = labelSortDirection === '-' ? 'desc' : 'asc';
        return sortLabels(filteredLabels, labelSort, direction);
    };

    getTableData = () => {
        const categories = this.props.categoryConfig?.categories || [];
        const filteredData = filterCategories(categories, this.props.searchQuery);
        const direction = this.props.sortDirection === '-' ? 'desc' : 'asc';
        return sortCategories(filteredData, this.props.sort, direction);
    };

    render() {
        const tableData = this.getTableData();
        const { selectedCategory, showDeleteConfirm } = this.state;

        return (
            <>
                <Prompt when={this.hasUnsavedChanges()} message={ARE_YOU_SURE_TEXT} />
                <ConfirmationModal
                    open={showDeleteConfirm}
                    onClose={this.handleCancelDeleteCategory}
                    onConfirm={this.handleConfirmDeleteCategory}
                    confirmText={getText(MODELS.EVENT_CLEARANCE_CATEGORY, 'tableConfirmDelete')}
                    confirmLabel="Delete"
                />
                <div className="ckp-event-category-config">
                    {!selectedCategory ? (
                        <CategoriesOverview
                            categoryConfig={this.props.categoryConfig}
                            tableData={tableData}
                            onOpenCategory={this.handleOpenCategory}
                            onAddCategory={this.handleAddCategory}
                            onBulkDeleteCategories={this.handleBulkDeleteCategories}
                            onPositionChange={this.handleCategoryPositionChange}
                        />
                    ) : (
                        <CategoryDetails
                            category={selectedCategory}
                            hasChanges={this.hasUnsavedChanges()}
                            isFormValid={this.isCategoryFormValid()}
                            isNewCategory={selectedCategory.id.startsWith('new-')}
                            onSave={this.handleSaveCategory}
                            onCancel={this.handleCloseCategory}
                            onDelete={this.handleDeleteCategory}
                            onFieldChange={this.handleCategoryFieldChange}
                            onVisibilityChange={this.handleVisibilityChange}
                            onAddLabel={this.handleAddLabel}
                            onLabelChange={(label, value) => this.handleLabelFieldChange(label, 'label', value)}
                            onColorChange={(label, value) => this.handleLabelFieldChange(label, 'color', value)}
                            onDefaultSelectedChange={this.handleDefaultSelectedChange}
                            onBulkDeleteLabels={this.handleBulkDeleteLabels}
                            labelTableData={this.getLabelTableData()}
                        />
                    )}
                </div>
            </>
        );
    }
}

const mapStateToProps = (state, props) => {
    const categoryTableIndex = 2;
    const labelTableIndex = 1;
    const categoryProps = { ...props, tableIndex: categoryTableIndex };
    const labelProps = { ...props, tableIndex: labelTableIndex };

    return {
        categoryConfig: formDataSelector(state, { model: MODELS.EVENT_CATEGORY_CONFIG }),
        sort: sortSelector(state, categoryProps),
        sortDirection: sortDirectionSelector(state, categoryProps),
        searchQuery: searchQuerySelector(state, categoryProps),
        labelSort: sortSelector(state, labelProps),
        labelSortDirection: sortDirectionSelector(state, labelProps),
        labelSearchQuery: searchQuerySelector(state, labelProps),
        categorySelection: selectedSelector(state, categoryProps),
        labelSelection: selectedSelector(state, labelProps)
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        { fetchCategoryConfig, saveCategoryConfig, select, resetSelection, blockTabSwitch, enableTabSwitch },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(CategoryConfig);
